/**
 * Recently Viewed Products Manager
 * Tracks last 10 products viewed by user, stored in localStorage
 * Auto-expires products after 7 days
 */

class RecentlyViewedManager {
  constructor() {
    // Konfiguracja
    this.storageKey = 'piano_recently_viewed';
    this.maxItems = 10;
    this.expiryDays = 7;
    this.isReady = false;

    console.log('[RecentlyViewed] Initializing manager');
    this.init();
  }

  init() {
    try {
      // Sprawdź dostępność localStorage
      if (!this.isLocalStorageAvailable()) {
        console.error('[RecentlyViewed] localStorage not available');
        return;
      }

      // Wyczyść expired products przy inicjalizacji
      this.cleanExpired();

      this.isReady = true;
      console.log('[RecentlyViewed] Manager initialized successfully');
      console.log('[RecentlyViewed] Current products count:', this.getProducts().length);

      // Async sprawdź czy produkty nadal istnieją i są dostępne w sklepie.
      // Fire-and-forget — render z cache idzie natychmiast, event 'validated'
      // wywoła re-render dla wszystkich instancji jeśli coś zostanie odfiltrowane.
      this.validateProducts();
    } catch (error) {
      console.error('[RecentlyViewed] Initialization error:', error);
    }
  }

  /**
   * Sprawdza w tle, czy produkty z localStorage nadal istnieją i są
   * dostępne (available === true). Usuwa wpisy zwracające 404
   * lub oznaczone jako niedostępne (sold out / draft).
   * Race-safe: filtrowanie operuje na ID, save merge'uje z bieżącą
   * listą (która mogła zmienić się między snapshotem a finishem).
   */
  async validateProducts() {
    const snapshot = this.getProducts();
    if (snapshot.length === 0) return;

    console.log('[RecentlyViewed] Validating', snapshot.length, 'products against store');

    const checks = await Promise.all(
      snapshot.map(async (p) => {
        const baseUrl = (p.url || '').split('?')[0].replace(/\.js$/, '');
        if (!baseUrl) return { id: p.id, drop: true, reason: 'missing url' };
        try {
          const res = await fetch(`${baseUrl}.js`, { headers: { Accept: 'application/json' } });
          if (res.status === 404 || res.status === 410) {
            return { id: p.id, drop: true, reason: `HTTP ${res.status}` };
          }
          if (!res.ok) {
            // 5xx / 429 — przejściowe, zostawiamy
            return { id: p.id, drop: false, reason: `HTTP ${res.status} (transient)` };
          }
          const fresh = await res.json();
          if (!fresh.available) return { id: p.id, drop: true, reason: 'unavailable' };
          return { id: p.id, drop: false };
        } catch (err) {
          // Network/parse error — przejściowe, zostawiamy
          return { id: p.id, drop: false, reason: `transient: ${err.message}` };
        }
      })
    );

    const invalidIds = [];
    checks.forEach((c, i) => {
      if (c.drop) {
        invalidIds.push(c.id);
        console.log('[RecentlyViewed] Dropping product', c.id, snapshot[i].handle, '—', c.reason);
      } else if (c.reason) {
        console.log('[RecentlyViewed] Keeping product', c.id, snapshot[i].handle, '—', c.reason);
      }
    });

    if (invalidIds.length === 0) {
      console.log('[RecentlyViewed] All', snapshot.length, 'products valid');
      return;
    }

    // Re-read po await: lista mogła się zmienić (np. addProduct na karcie produktu)
    const current = this.getProducts();
    const filtered = current.filter(p => !invalidIds.includes(p.id));

    if (filtered.length !== current.length) {
      this.saveProducts(filtered);
      console.log('[RecentlyViewed] Removed', current.length - filtered.length, 'invalid products');
      this.dispatchUpdateEvent('validated', null, filtered);
    }
  }

  /**
   * Sprawdza dostępność localStorage
   * @returns {boolean}
   */
  isLocalStorageAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Dodaje produkt do listy ostatnio oglądanych
   * Jeśli produkt już istnieje, przenosi go na początek (LIFO)
   * @param {Object} productData - Dane produktu
   */
  addProduct(productData) {
    console.log('[RecentlyViewed] addProduct called with:', productData);

    // Walidacja danych
    if (!this.validateProductData(productData)) {
      console.error('[RecentlyViewed] Invalid product data:', productData);
      return;
    }

    try {
      // Pobierz aktualną listę
      let products = this.getProducts();
      console.log('[RecentlyViewed] Current products before add:', products.length);

      // Usuń produkt jeśli już istnieje (aby przenieść na początek)
      const existingIndex = products.findIndex(p => p.id === productData.id);
      if (existingIndex !== -1) {
        console.log('[RecentlyViewed] Product already exists at index', existingIndex, '- moving to top');
        products.splice(existingIndex, 1);
      }

      // Dodaj na początek (LIFO - Last In First Out)
      const productToAdd = {
        ...productData,
        timestamp: Date.now()
      };
      products.unshift(productToAdd);
      console.log('[RecentlyViewed] Product added to top of list');

      // Ogranicz do maxItems
      if (products.length > this.maxItems) {
        const removed = products.length - this.maxItems;
        products = products.slice(0, this.maxItems);
        console.log('[RecentlyViewed] List trimmed, removed', removed, 'oldest products');
      }

      // Zapisz do localStorage
      this.saveProducts(products);
      console.log('[RecentlyViewed] Products saved. Total count:', products.length);
      console.log('[RecentlyViewed] Current list:', products.map(p => ({ id: p.id, title: p.title })));

      // Dispatch custom event
      this.dispatchUpdateEvent('product-added', productToAdd, products);

    } catch (error) {
      console.error('[RecentlyViewed] Error adding product:', error);
    }
  }

  /**
   * Waliduje dane produktu
   * @param {Object} productData
   * @returns {boolean}
   */
  validateProductData(productData) {
    const requiredFields = ['id', 'handle', 'title', 'price', 'image_url', 'url'];

    for (const field of requiredFields) {
      if (!productData[field]) {
        console.error('[RecentlyViewed] Missing required field:', field);
        return false;
      }
    }

    return true;
  }

  /**
   * Pobiera listę ostatnio oglądanych produktów
   * Automatycznie czyści expired products
   * @returns {Array}
   */
  getProducts() {
    try {
      const data = localStorage.getItem(this.storageKey);

      if (!data) {
        console.log('[RecentlyViewed] No products in storage');
        return [];
      }

      let products = JSON.parse(data);

      // Filtruj expired products
      const now = Date.now();
      const expiryMs = this.expiryDays * 24 * 60 * 60 * 1000;
      const beforeCount = products.length;

      products = products.filter(product => {
        const age = now - product.timestamp;
        return age < expiryMs;
      });

      const cleanedCount = beforeCount - products.length;
      if (cleanedCount > 0) {
        console.log('[RecentlyViewed] Cleaned', cleanedCount, 'expired products');
        this.saveProducts(products);
      }

      return products;

    } catch (error) {
      console.error('[RecentlyViewed] Error getting products:', error);
      return [];
    }
  }

  /**
   * Zapisuje listę produktów do localStorage
   * @param {Array} products
   */
  saveProducts(products) {
    try {
      const data = JSON.stringify(products);
      localStorage.setItem(this.storageKey, data);
      console.log('[RecentlyViewed] Products saved to localStorage');
    } catch (error) {
      console.error('[RecentlyViewed] Error saving products:', error);

      // Jeśli błąd QuotaExceededError, usuń najstarsze produkty
      if (error.name === 'QuotaExceededError') {
        console.warn('[RecentlyViewed] localStorage quota exceeded, reducing list');
        const reducedProducts = products.slice(0, 5);
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(reducedProducts));
          console.log('[RecentlyViewed] List reduced to 5 items');
        } catch (e) {
          console.error('[RecentlyViewed] Still cannot save:', e);
        }
      }
    }
  }

  /**
   * Czyści produkty starsze niż expiryDays
   */
  cleanExpired() {
    console.log('[RecentlyViewed] Cleaning expired products (older than', this.expiryDays, 'days)');
    const products = this.getProducts(); // getProducts już filtruje expired
    console.log('[RecentlyViewed] Products after cleanup:', products.length);
  }

  /**
   * Czyści całą listę
   */
  clear() {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('[RecentlyViewed] All products cleared');
      this.dispatchUpdateEvent('cleared', null, []);
    } catch (error) {
      console.error('[RecentlyViewed] Error clearing products:', error);
    }
  }

  /**
   * Usuwa konkretny produkt z listy
   * @param {number} productId
   */
  removeProduct(productId) {
    console.log('[RecentlyViewed] Removing product:', productId);

    try {
      let products = this.getProducts();
      const beforeCount = products.length;

      products = products.filter(p => p.id !== productId);

      if (products.length < beforeCount) {
        this.saveProducts(products);
        console.log('[RecentlyViewed] Product removed. Remaining:', products.length);
        this.dispatchUpdateEvent('product-removed', { id: productId }, products);
      } else {
        console.log('[RecentlyViewed] Product not found in list');
      }
    } catch (error) {
      console.error('[RecentlyViewed] Error removing product:', error);
    }
  }

  /**
   * Dispatch custom event dla update'ów
   * @param {string} action
   * @param {Object} product
   * @param {Array} list
   */
  dispatchUpdateEvent(action, product, list) {
    try {
      window.dispatchEvent(new CustomEvent('recently-viewed:updated', {
        detail: {
          action: action,
          product: product,
          list: list,
          count: list.length
        }
      }));
      console.log('[RecentlyViewed] Event dispatched:', action);
    } catch (error) {
      console.error('[RecentlyViewed] Error dispatching event:', error);
    }
  }

  /**
   * Zwraca statystyki
   * @returns {Object}
   */
  getStats() {
    const products = this.getProducts();
    const now = Date.now();

    const stats = {
      total: products.length,
      oldest: products.length > 0 ? Math.floor((now - products[products.length - 1].timestamp) / (1000 * 60 * 60 * 24)) : 0,
      newest: products.length > 0 ? Math.floor((now - products[0].timestamp) / (1000 * 60 * 60 * 24)) : 0
    };

    console.log('[RecentlyViewed] Stats:', stats);
    return stats;
  }
}

// Inicjalizacja globalna (wykonywane automatycznie przy ładowaniu skryptu)
document.addEventListener('DOMContentLoaded', function() {
  console.log('[RecentlyViewed] DOMContentLoaded - creating manager instance');
  window.recentlyViewedManager = new RecentlyViewedManager();
});

/**
 * Globalna funkcja helper do dodawania produktów
 * Może być wywoływana z dowolnego miejsca w kodzie
 * @param {Object} productData
 */
window.addToRecentlyViewed = function(productData) {
  console.log('[RecentlyViewed] Global function called:', productData);

  // Retry logic - czekaj aż manager będzie ready
  let attempts = 0;
  const maxAttempts = 50;

  function tryAdd() {
    if (window.recentlyViewedManager?.isReady) {
      console.log('[RecentlyViewed] Manager ready, adding product');
      window.recentlyViewedManager.addProduct(productData);
    } else {
      attempts++;
      if (attempts < maxAttempts) {
        console.log('[RecentlyViewed] Manager not ready yet, retry', attempts, '/', maxAttempts);
        setTimeout(tryAdd, 100);
      } else {
        console.error('[RecentlyViewed] Manager not ready after', maxAttempts, 'attempts');
      }
    }
  }

  tryAdd();
};

console.log('[RecentlyViewed] Script loaded');
